import { useState, useEffect } from 'react';

export const useRestaurantData = () => {
  const [maxResults, setMaxResults] = useState(500);
  const [loading, setLoading] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [executionTime, setExecutionTime] = useState(null);
  const [operatorDataAvailable, setOperatorDataAvailable] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  // ── AI Intelligence state ──────────────────────────────────────────────────
  const [aiLoading,    setAiLoading]    = useState(false);
  const [aiResult,     setAiResult]     = useState(null);
  const [aiError,      setAiError]      = useState(null);
  const [aiActive,     setAiActive]     = useState(false);

  // ── Batch script state ─────────────────────────────────────────────────────
  const [aiScripts,    setAiScripts]    = useState([]);
  const [aiHasMore,    setAiHasMore]    = useState(false);
  const [aiOffset,     setAiOffset]     = useState(0);
  const [aiTotalFound, setAiTotalFound] = useState(0);
  const [aiCurrentQuery, setAiCurrentQuery] = useState('');
  // ──────────────────────────────────────────────────────────────────────────

  const [backendHealth, setBackendHealth] = useState(null);
  const [dataSources, setDataSources] = useState(null);
  const [governmentCities, setGovernmentCities] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    loadDataSources();
    loadGovernmentCities();
    checkBackendHealth();
  }, []);

  useEffect(() => {
    applyFilter(activeFilter, restaurants);
  }, [restaurants, activeFilter]);

  const loadDataSources = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/sources`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) setDataSources(data);
      }
    } catch (error) { console.error('Failed to load data sources:', error); }
  };

  const loadGovernmentCities = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/government-cities`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.cities) setGovernmentCities(data.cities);
      }
    } catch (error) { console.error('Failed to load government cities:', error); }
  };

  const cityHasGovernmentData = (cityName) => {
    if (!cityName || !Array.isArray(governmentCities)) return false;
    return governmentCities.some(city => {
      const govCityName = typeof city === 'string' ? city : (city.name || city.city || '');
      if (!govCityName || typeof govCityName !== 'string') return false;
      const inputCity = String(cityName).toLowerCase();
      const govCity   = govCityName.toLowerCase();
      return govCity.includes(inputCity) || inputCity.includes(govCity);
    });
  };

  // ── borough param added — null means backend picks randomly ───────────────
  const fetchRestaurantData = async (e, city, country = 'United States', seenIds = [], maxLeads = maxResults, borough = null) => {
    e?.preventDefault();
    if (!city?.trim()) { setError('Please select a city'); return; }

    setLoading(true);
    setError("");
    setRestaurants([]);
    setFilteredRestaurants([]);
    setStats(null);
    setSearchLocation(null);
    setExecutionTime(null);
    setActiveFilter('all');

    try {
      const response = await fetch(`${API_BASE_URL}/restaurants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          city: city.trim(),
          country: country.trim(),
          options: { maxResults: maxLeads },
          seenIds,
          borough, // null = random, "brooklyn" etc = specific borough
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
      if (!data.success) throw new Error(data.error || 'Unknown error occurred');

      const processedRestaurants = data.data.restaurants.map(restaurant => ({
        ...restaurant,
        formattedPhone:    formatPhoneNumber(restaurant.phone),
        formattedWebsite:  formatWebsiteUrl(restaurant.website),
        isValidEmail:      isValidEmail(restaurant.email),
        completenessScore: calculateCompletenessScore(restaurant),
      }));

      setRestaurants(processedRestaurants);
      setStats(data.data.stats);
      setSearchLocation(data.data.searchLocation);
      setExecutionTime(data.data.executionTime);
      setOperatorDataAvailable(data.data.operatorDataAvailable || false);

    } catch (error) {
      console.error('Restaurant data fetch error:', error);
      setError(error.message || 'An error occurred while fetching restaurant data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (filter, data) => {
    let result = data;
    switch (filter) {
      case 'phone':         result = data.filter(r => r.phone); break;
      case 'email':         result = data.filter(r => r.email && r.isValidEmail); break;
      case 'website':       result = data.filter(r => r.website); break;
      case 'highQuality':   result = data.filter(r => r.completenessScore >= 12); break;
      case 'hasOperator':   result = data.filter(r => r.operatorData); break;
      case 'operatorPhone': result = data.filter(r => r.operatorData?.operatorPhone); break;
      case 'gradeA':        result = data.filter(r => r.operatorData?.healthGrade === 'A'); break;
      default:               result = data;
    }
    setFilteredRestaurants(result);
  };

  const setFilter = (filter) => {
    setActiveFilter(filter);
    applyFilter(filter, restaurants);
  };

  // ── Clean XLSX Export via SheetJS ──────────────────────────────────────────
  const exportToCSV = async (data, cityName = 'restaurants') => {
    let XLSX;
    try {
      XLSX = await import('xlsx');
    } catch {
      exportToPlainCSV(data, cityName);
      return;
    }

    const headers = [
      'Name', 'Address', 'Business Phone', 'Owner Direct Phone',
      'Restaurant Grade', 'DBA / LLC Name', 'Website', 'Email', 'Cuisine', 'Score',
    ];

    const rows = data.map(r => {
      const op = r.operatorData || {};
      return [
        r.name    || '',
        r.address || '',
        r.phone   || '',
        op.operatorPhone || '',
        op.healthGrade   || '',
        op.dba           || '',
        r.website || '',
        r.email   || '',
        r.operatorData?.healthCuisine || r.cuisine || '',
        r.completenessScore || 0,
      ];
    });

    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws['!cols'] = [
      { wch: 28 }, { wch: 32 }, { wch: 18 }, { wch: 20 }, { wch: 16 },
      { wch: 26 }, { wch: 30 }, { wch: 30 }, { wch: 20 }, { wch: 8  },
    ];
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };

    const headerStyle = {
      font:      { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
      fill:      { fgColor: { rgb: '111111' }, patternType: 'solid' },
      alignment: { horizontal: 'center', vertical: 'center' },
      border:    { bottom: { style: 'thin', color: { rgb: '444444' } } },
    };
    headers.forEach((_, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
      if (ws[cellRef]) ws[cellRef].s = headerStyle;
    });

    rows.forEach((_, rowIdx) => {
      const fillColor = rowIdx % 2 === 0 ? 'FAFAFA' : 'FFFFFF';
      headers.forEach((_, colIdx) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIdx + 1, c: colIdx });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            font:      { sz: 10 },
            fill:      { fgColor: { rgb: fillColor }, patternType: 'solid' },
            alignment: { vertical: 'center', wrapText: false },
          };
        }
      });
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    const filename = `NYLeads_${cityName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const exportToPlainCSV = (data, cityName) => {
    const headers = [
      'Name', 'Address', 'Business Phone', 'Owner Direct Phone',
      'Restaurant Grade', 'DBA / LLC Name', 'Website', 'Email', 'Cuisine', 'Score',
    ];
    const csvContent = [
      headers.join(','),
      ...data.map(r => {
        const op = r.operatorData || {};
        return [
          `"${(r.name    || '').replace(/"/g, '""')}"`,
          `"${(r.address || '').replace(/"/g, '""')}"`,
          `"${(r.phone   || '').replace(/"/g, '""')}"`,
          `"${(op.operatorPhone || '').replace(/"/g, '""')}"`,
          `"${(op.healthGrade   || '').replace(/"/g, '""')}"`,
          `"${(op.dba           || '').replace(/"/g, '""')}"`,
          `"${(r.website || '').replace(/"/g, '""')}"`,
          `"${(r.email   || '').replace(/"/g, '""')}"`,
          `"${(r.operatorData?.healthCuisine || r.cuisine || '').replace(/"/g, '""')}"`,
          r.completenessScore || 0,
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `NYLeads_${cityName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
      .then(() => console.log(`${label} copied to clipboard`))
      .catch(err => console.error('Failed to copy:', err));
  };

  const openInMaps = (lat, lon, name, address) => {
    const query = (lat && lon) ? `${lat},${lon}` : (address || name);
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
      '_blank'
    );
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10)
      return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    if (cleaned.length === 11 && cleaned[0] === '1')
      return `+1 (${cleaned.slice(1,4)}) ${cleaned.slice(4,7)}-${cleaned.slice(7)}`;
    return phone;
  };

  const isValidEmail = (email) => {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const formatWebsiteUrl = (website) => {
    if (!website) return '';
    if (!website.startsWith('http://') && !website.startsWith('https://'))
      return `https://${website}`;
    return website;
  };

  const calculateCompletenessScore = (restaurant) => {
    let score = 0;
    if (restaurant.name?.trim() && restaurant.name !== 'Unknown Restaurant') score += 2;
    if (restaurant.phone?.trim())   score += 5;
    if (restaurant.email?.trim())   score += 5;
    if (restaurant.website?.trim()) score += 3;
    if (restaurant.address?.trim() && restaurant.address !== 'Address not available') score += 2;
    if (restaurant.lat && restaurant.lon) score += 2;
    if (restaurant.cuisine?.trim()) score += 1;
    if (restaurant.operatorData?.operatorPhone) score += 2;
    return score;
  };

  const checkBackendHealth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      const data = await response.json();
      setBackendHealth(data);
      return data;
    } catch (error) {
      setBackendHealth({ status: 'unhealthy', error: error.message });
      return null;
    }
  };

  const getHighQualityRestaurants    = (data, minScore = 12) => data.filter(r => r.completenessScore >= minScore);
  const getRestaurantsWithContact    = (data, type) => {
    switch (type) {
      case 'phone':   return data.filter(r => r.phone);
      case 'email':   return data.filter(r => r.email && r.isValidEmail);
      case 'website': return data.filter(r => r.website);
      case 'all':     return data.filter(r => r.phone && r.email && r.website);
      default:        return data;
    }
  };
  const getRestaurantsByCompleteness = (data) => ({
    excellent: data.filter(r => r.completenessScore >= 15),
    good:      data.filter(r => r.completenessScore >= 10 && r.completenessScore < 15),
    basic:     data.filter(r => r.completenessScore < 10)
  });

  const getRestaurantsWithOperatorData  = (data) => data.filter(r => r.operatorData);
  const getRestaurantsWithOperatorPhone = (data) => data.filter(r => r.operatorData?.operatorPhone);
  const getRestaurantsWithGradeA        = (data) => data.filter(r => r.operatorData?.healthGrade === 'A');

  // ── AI Intelligence ────────────────────────────────────────────────────────

  const _fetchAI = async (query, offset) => {
    const response = await fetch(`${API_BASE_URL}/ai-intelligence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, restaurants, offset })
    });
    const data = await response.json();
    if (!response.ok || !data.success) throw new Error(data.error || 'AI request failed');
    return data.data;
  };

  const queryAI = async (query) => {
    if (!query?.trim()) return;
    if (!restaurants.length) {
      setAiError('No restaurant data loaded. Please fetch a city first.');
      return;
    }
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    setAiActive(true);
    setAiScripts([]);
    setAiHasMore(false);
    setAiOffset(0);
    setAiTotalFound(0);
    setAiCurrentQuery(query);

    try {
      const result = await _fetchAI(query, 0);
      setAiResult(result);
      if (result.script?.length > 0) setAiScripts(result.script);
      setAiHasMore(result.hasMore || false);
      setAiOffset(result.offset   || 0);
      setAiTotalFound(result.totalFound || 0);
    } catch (err) {
      console.error('AI query error:', err);
      setAiError(err.message || 'AI request failed');
    } finally {
      setAiLoading(false);
    }
  };

  const loadMoreScripts = async () => {
    if (!aiCurrentQuery || !aiHasMore || aiLoading) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await _fetchAI(aiCurrentQuery, aiOffset);
      if (result.script?.length > 0) setAiScripts(prev => [...prev, ...result.script]);
      setAiResult(result);
      setAiHasMore(result.hasMore || false);
      setAiOffset(result.offset   || 0);
      setAiTotalFound(result.totalFound || 0);
    } catch (err) {
      console.error('Load more scripts error:', err);
      setAiError(err.message || 'Failed to load more scripts');
    } finally {
      setAiLoading(false);
    }
  };

  const clearAI = () => {
    setAiResult(null);
    setAiError(null);
    setAiActive(false);
    setAiLoading(false);
    setAiScripts([]);
    setAiHasMore(false);
    setAiOffset(0);
    setAiTotalFound(0);
    setAiCurrentQuery('');
  };

  return {
    maxResults, loading, restaurants, filteredRestaurants,
    setRestaurants,
    error, stats, searchLocation, executionTime,
    backendHealth, dataSources, governmentCities,
    operatorDataAvailable, activeFilter,
    setMaxResults, setFilter,
    fetchRestaurantData, loadDataSources, loadGovernmentCities,
    exportToCSV, copyToClipboard, openInMaps,
    formatPhoneNumber, formatWebsiteUrl, isValidEmail,
    calculateCompletenessScore, checkBackendHealth,
    getHighQualityRestaurants, getRestaurantsWithContact,
    getRestaurantsByCompleteness, cityHasGovernmentData,
    getRestaurantsWithOperatorData,
    getRestaurantsWithOperatorPhone,
    getRestaurantsWithGradeA,
    queryAI, clearAI, loadMoreScripts,
    aiLoading, aiResult, aiError, aiActive,
    aiScripts, aiHasMore, aiOffset, aiTotalFound,
    setAiResult, setAiActive,
  };
};